import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

// Heidi (24-Apr): "the average on how often they get all their tasks done
// on the days they're set over the past 30 days. It will just help admins
// and site managers to see who their productive workers are."
//
// Definition lives in workerService.getCompletionRates: % of days in the
// window where every assigned task was completed. Days with no tasks are
// excluded; workers with zero days of tasks render as "Not yet scheduled".

const ScoreBadge = ({ score }) => {
  const color = score >= 90 ? 'bg-emerald-100 text-emerald-800'
    : score >= 70 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800';
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${color}`}>
      {score}%
    </span>
  );
};

const ScoreBar = ({ score }) => {
  const fill = score >= 90 ? 'bg-emerald-500' : score >= 70 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="w-24 bg-muted rounded-full h-1.5 overflow-hidden">
      <div className={`h-full rounded-full ${fill}`} style={{ width: `${score}%` }} />
    </div>
  );
};

const WorkerPerformanceTable = ({ workers, loading, onWorkerClick }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleRowClick = (workerId) => {
    if (onWorkerClick) onWorkerClick(workerId);
    else navigate(`/workers/${workerId}`);
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-foreground font-heading">{t('workerPerformance')}</h3>
          <p className="text-sm text-muted-foreground">{t('last30Days')} — {t('clickRowToViewProfile')}</p>
        </div>
        <button
          onClick={() => navigate('/workers')}
          className="text-sm text-accent hover:text-accent/80 font-medium flex items-center gap-1"
        >
          {t('viewAll')} <Icon name="ArrowRight" size={14} />
        </button>
      </div>

      {loading ? (
        <div className="px-6 py-8 flex items-center justify-center">
          <Icon name="Loader2" size={20} className="text-muted-foreground animate-spin" />
        </div>
      ) : !workers?.length ? (
        <div className="px-6 py-8 text-center text-sm text-muted-foreground">
          {t('noWorkersYet')}
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('worker')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('completionRate')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('daysWithTasks')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {workers?.map((w) => (
                  <tr
                    key={w?.user_id}
                    onClick={() => handleRowClick(w?.user_id)}
                    className="hover:bg-muted/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-accent">
                            {(w?.full_name || '?')?.split(' ')?.map(n => n?.[0])?.join('')}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-foreground text-sm">{w?.full_name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{w?.role}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {w?.completion_pct === null ? (
                        <span className="text-sm text-muted-foreground italic">{t('notYetScheduled')}</span>
                      ) : (
                        <div className="flex items-center gap-3">
                          <ScoreBar score={w?.completion_pct} />
                          <ScoreBadge score={w?.completion_pct} />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {w?.completion_pct === null
                        ? '—'
                        : `${w?.completion_days} / ${w?.scheduled_days}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-border">
            {workers?.map((w) => (
              <div
                key={w?.user_id}
                onClick={() => handleRowClick(w?.user_id)}
                className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-accent">
                        {(w?.full_name || '?')?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{w?.full_name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{w?.role}</p>
                    </div>
                  </div>
                  {w?.completion_pct === null ? (
                    <span className="text-xs text-muted-foreground italic">{t('notYetScheduled')}</span>
                  ) : (
                    <ScoreBadge score={w?.completion_pct} />
                  )}
                </div>
                {w?.completion_pct !== null && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <ScoreBar score={w?.completion_pct} />
                    <span>{w?.completion_days} / {w?.scheduled_days} {t('daysCompleted')}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default WorkerPerformanceTable;
